/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Pickers used across the Real Estate screens. All are controlled by id:
 * `value` is an id (or null) and `onChange(id, record)`.
 *
 * - RePicker      server-side search against a `/re/*` list endpoint
 * - CorePicker    core CRM lists (contacts, leads) that have no search param:
 *                 loads `?all=true` once when first opened and filters locally
 * - ContactPicker CorePicker for contacts, with optional quick-create via the
 *                 app's existing ContactFormModal
 * - UserPicker    CRM users (/users/)
 * - VocabSelect   a plain Select over a vocabulary array
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Autocomplete, CircularProgress, FormControl, Icon, IconButton, InputAdornment, InputLabel, MenuItem, Select, TextField,
  Tooltip,
} from '@mui/material';
import MDBox from 'components/MDBox';
import ContactFormModal from 'components/NewContactModal';
import { FilterSelect } from 'components/v2/filters';
import { v2 as t, V2_FONT_FAMILY } from 'styles/tokens/v2';
import {
  cleanParams, contactLabel, errorMessage, humanize, useReApi, userLabel, useUsers,
} from '../api';
import { useIsReV2 } from '../../v2/parts';
import { useInReFilterBar } from './filterContext';

/**
 * UI v2: inside a page's filter row (RePage FilterBar) the pickers render the
 * v2 filter controls; everywhere else (dialogs, wizard steps) they stay the
 * MUI inputs, which theme-v2 already sizes. Values and handlers are the same.
 */
const useAsV2Filter = () => {
  const isV2 = useIsReV2();
  const inBar = useInReFilterBar();
  return isV2 && inBar;
};

/** "Status: All" options for FilterSelect, with the empty choice first. */
const selectOptions = (allowEmpty, emptyLabel, items) => [
  ...(allowEmpty ? [{ value: '', label: emptyLabel }] : []),
  ...items,
];

// ---------------------------------------------------------------------------

export function RePicker({
  path, label, value, onChange, getLabel, params, size, disabled, required, error, helperText, sx, initialLabel,
  filterOptions,
}) {
  const asFilter = useAsV2Filter();
  const { api, ready } = useReApi();
  const [input, setInput] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const paramsKey = JSON.stringify(cleanParams(params || {}));
  const timer = useRef(null);

  // Resolve the label of an externally supplied id.
  useEffect(() => {
    if (!value) {
      setSelected(null);
      return undefined;
    }
    if (selected && selected.id === value) return undefined;
    const known = options.find((o) => o.id === value);
    if (known) {
      setSelected(known);
      return undefined;
    }
    if (initialLabel) {
      setSelected({ id: value, __label: initialLabel });
      return undefined;
    }
    if (!ready) return undefined;
    let cancelled = false;
    api.get(`${path}/${value}`)
      .then((res) => { if (!cancelled) setSelected(res.data); })
      .catch(() => { if (!cancelled) setSelected({ id: value, __label: `#${value}` }); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, ready, path]);

  useEffect(() => {
    if (!ready) return undefined;
    let cancelled = false;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setLoading(true);
      api.get(path, { params: { page: 1, page_size: 50, ...JSON.parse(paramsKey), ...(input ? { search: input } : {}) } })
        .then((res) => {
          if (cancelled) return;
          const data = res.data || {};
          setOptions(Array.isArray(data) ? data : data.items || []);
        })
        .catch(() => { if (!cancelled) setOptions([]); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer.current);
    };
  }, [api, ready, path, paramsKey, input]);

  const labelOf = (o) => (o ? o.__label || getLabel(o) : '');
  const visible = filterOptions ? options.filter(filterOptions) : options;

  return (
    <Autocomplete
      // v2 filter row: full-size control (36px) so it lines up with the
      // FilterSelect / FilterSearch / DateField beside it.
      size={asFilter ? 'medium' : size}
      // Without a width from the screen, a picker in the v2 filter row is 180
      // wide (like the v2 filter selects); elsewhere it keeps 220.
      sx={sx || (asFilter ? { minWidth: 180, width: 180 } : { minWidth: 220 })}
      disabled={disabled}
      options={visible}
      value={selected}
      loading={loading}
      filterOptions={(x) => x}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      getOptionLabel={labelOf}
      onChange={(e, v) => {
        setSelected(v);
        onChange(v ? v.id : null, v);
      }}
      onInputChange={(e, v, reason) => {
        if (reason === 'input') setInput(v);
        if (reason === 'clear') setInput('');
      }}
      renderInput={(p) => (
        <TextField
          {...p}
          label={label}
          required={required}
          error={Boolean(error)}
          helperText={helperText}
          {...(asFilter ? {
            // v2 filter row: no floating label. The label is the placeholder
            // and accessible name, and an inline prefix once a value is chosen.
            label: undefined,
            placeholder: label,
            inputProps: { ...p.inputProps, 'aria-label': label },
          } : {})}
          InputProps={{
            ...p.InputProps,
            ...(asFilter && selected ? {
              startAdornment: (
                <InputAdornment position="start" sx={{ ml: 0.5, mr: 0, '& p': { fontSize: 14, color: t.text2, fontFamily: V2_FONT_FAMILY } }}>
                  {`${label}:`}
                </InputAdornment>
              ),
            } : {}),
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {p.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

RePicker.propTypes = {
  path: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func.isRequired,
  getLabel: PropTypes.func,
  params: PropTypes.object,
  size: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.node,
  sx: PropTypes.object,
  initialLabel: PropTypes.string,
  filterOptions: PropTypes.func,
};

RePicker.defaultProps = {
  value: null,
  getLabel: (o) => o.name || o.unit_number || o.booking_number || `#${o.id}`,
  params: {},
  size: 'small',
  disabled: false,
  required: false,
  error: false,
  helperText: null,
  sx: undefined,
  initialLabel: null,
  filterOptions: null,
};

export const unitLabel = (u) => {
  if (!u) return '';
  const bits = [u.unit_number];
  if (u.building_name) bits.push(u.building_name);
  if (u.project_name) bits.push(u.project_name);
  return bits.filter(Boolean).join(' · ');
};

// ---------------------------------------------------------------------------

export function CorePicker({
  path, label, value, onChange, getLabel, size, disabled, required, error, helperText, sx, extraOptions, endAction,
}) {
  const { api, ready } = useReApi();
  const [all, setAll] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = () => {
    if (all !== null || loading || !ready) return;
    setLoading(true);
    api.get(path)
      .then((res) => setAll(Array.isArray(res.data) ? res.data : res.data?.items || []))
      .catch(() => setAll([]))
      .finally(() => setLoading(false));
  };

  // Need the list to show the label of a preset value.
  useEffect(() => {
    if (value && all === null) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, ready]);

  const options = useMemo(() => {
    const base = all || [];
    const extra = (extraOptions || []).filter((x) => !base.some((b) => b.id === x.id));
    return [...extra, ...base];
  }, [all, extraOptions]);

  const selected = useMemo(() => options.find((o) => o.id === value) || null, [options, value]);

  return (
    <MDBox display="flex" alignItems="center" gap={0.5} sx={sx}>
      <Autocomplete
        size={size}
        sx={{ flex: 1, minWidth: 0 }}
        disabled={disabled}
        options={options}
        value={selected}
        loading={loading}
        onOpen={load}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        getOptionLabel={(o) => getLabel(o)}
        filterOptions={(opts, state) => {
          const q = state.inputValue.trim().toLowerCase();
          const matched = q
            ? opts.filter((o) => [getLabel(o), o.email, o.phone, o.mobile_number].filter(Boolean)
              .some((s) => String(s).toLowerCase().includes(q)))
            : opts;
          return matched.slice(0, 100);
        }}
        renderOption={(props, o) => (
          <li {...props} key={o.id}>
            <MDBox>
              <MDBox component="div" sx={{ fontSize: '0.85rem' }}>{getLabel(o)}</MDBox>
              {(o.email || o.phone) && (
                <MDBox component="div" sx={{ fontSize: '0.72rem', color: '#5C6B82' }}>
                  {[o.email, o.phone || o.mobile_number].filter(Boolean).join(' · ')}
                </MDBox>
              )}
            </MDBox>
          </li>
        )}
        onChange={(e, v) => onChange(v ? v.id : null, v)}
        renderInput={(p) => (
          <TextField
            {...p}
            label={label}
            required={required}
            error={Boolean(error)}
            helperText={helperText}
            InputProps={{
              ...p.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={16} /> : null}
                  {p.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {endAction}
    </MDBox>
  );
}

CorePicker.propTypes = {
  path: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func.isRequired,
  getLabel: PropTypes.func,
  size: PropTypes.string,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.node,
  sx: PropTypes.object,
  extraOptions: PropTypes.array,
  endAction: PropTypes.node,
};

CorePicker.defaultProps = {
  value: null,
  getLabel: contactLabel,
  size: 'small',
  disabled: false,
  required: false,
  error: false,
  helperText: null,
  sx: { minWidth: 240 },
  extraOptions: [],
  endAction: null,
};

// ---------------------------------------------------------------------------

export function ContactPicker({ allowCreate, onNotify, ...rest }) {
  const { api, user } = useReApi();
  const users = useUsers();
  const [open, setOpen] = useState(false);
  const [created, setCreated] = useState([]);

  const handleSave = async (formData) => {
    try {
      const now = new Date().toISOString();
      const res = await api.post('/contacts/', {
        ...formData, created_at: now, updated_at: now, last_activity: now,
      });
      setCreated((c) => [res.data, ...c]);
      rest.onChange(res.data.id, res.data);
      setOpen(false);
      if (onNotify) onNotify('Contact created');
    } catch (err) {
      if (onNotify) onNotify(errorMessage(err, 'Could not create contact'), 'error');
      throw err;
    }
  };

  return (
    <>
      <CorePicker
        path="/contacts/?all=true"
        getLabel={contactLabel}
        extraOptions={created}
        endAction={allowCreate ? (
          <Tooltip title="New contact">
            <IconButton size="small" onClick={() => setOpen(true)}>
              <Icon>person_add</Icon>
            </IconButton>
          </Tooltip>
        ) : null}
        {...rest}
      />
      {allowCreate && open && (
        <ContactFormModal
          isOpen={open}
          onClose={() => setOpen(false)}
          onSave={handleSave}
          managers={users}
          user={user}
          title="Quick-create contact"
        />
      )}
    </>
  );
}

ContactPicker.propTypes = {
  allowCreate: PropTypes.bool,
  onNotify: PropTypes.func,
  onChange: PropTypes.func.isRequired,
};

ContactPicker.defaultProps = { allowCreate: false, onNotify: null };

export const leadLabel = (l) => {
  if (!l) return '';
  const full = [l.first_name, l.last_name].filter(Boolean).join(' ').trim();
  return full || l.name || l.company || l.email || `Lead #${l.id}`;
};

export function LeadPicker(props) {
  return <CorePicker path="/leads/?all=true" getLabel={leadLabel} {...props} />;
}

// ---------------------------------------------------------------------------

export function UserPicker({ label, value, onChange, size, sx, allowEmpty, emptyLabel }) {
  const users = useUsers();
  const asFilter = useAsV2Filter();
  if (asFilter) {
    return (
      <FilterSelect
        label={label}
        value={value ?? ''}
        onChange={(v) => onChange(v === '' ? null : v)}
        options={selectOptions(allowEmpty, emptyLabel, users.map((u) => ({ value: u.id, label: userLabel(u) })))}
        minWidth={sx?.minWidth}
      />
    );
  }
  return (
    <FormControl size={size} sx={sx}>
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        sx={{ height: size === 'small' ? 40 : undefined }}
      >
        {allowEmpty && <MenuItem value="">{emptyLabel}</MenuItem>}
        {users.map((u) => (
          <MenuItem key={u.id} value={u.id}>{userLabel(u)}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

UserPicker.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func.isRequired,
  size: PropTypes.string,
  sx: PropTypes.object,
  allowEmpty: PropTypes.bool,
  emptyLabel: PropTypes.string,
};

UserPicker.defaultProps = {
  label: 'Agent', value: null, size: 'small', sx: { minWidth: 180 }, allowEmpty: true, emptyLabel: 'Any',
};

export function VocabSelect({
  label, value, onChange, values, size, sx, allowEmpty, emptyLabel, labelFor, required, disabled,
}) {
  const asFilter = useAsV2Filter();
  if (asFilter) {
    return (
      <FilterSelect
        label={label}
        value={value ?? ''}
        onChange={(v) => onChange(v === '' ? null : v)}
        options={selectOptions(allowEmpty, emptyLabel, values.map((v) => ({ value: v, label: labelFor ? labelFor(v) : humanize(v) })))}
        disabled={disabled}
        minWidth={sx?.minWidth}
      />
    );
  }
  return (
    <FormControl size={size} sx={sx} required={required} disabled={disabled}>
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        sx={{ height: size === 'small' ? 40 : undefined }}
      >
        {allowEmpty && <MenuItem value="">{emptyLabel}</MenuItem>}
        {values.map((v) => (
          <MenuItem key={v} value={v}>{labelFor ? labelFor(v) : humanize(v)}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

VocabSelect.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  values: PropTypes.array.isRequired,
  size: PropTypes.string,
  sx: PropTypes.object,
  allowEmpty: PropTypes.bool,
  emptyLabel: PropTypes.string,
  labelFor: PropTypes.func,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
};

VocabSelect.defaultProps = {
  value: null,
  size: 'small',
  sx: { minWidth: 160 },
  allowEmpty: true,
  emptyLabel: 'All',
  labelFor: null,
  required: false,
  disabled: false,
};
